'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { CrewMember } from './CalendarInteractive';

interface FilterBarProps {
  crew: CrewMember[];
  filterStatus: string[];
  filterCrew: string[];
  filterType: string[];
  onStatusChange: (v: string[]) => void;
  onCrewChange: (v: string[]) => void;
  onTypeChange: (v: string[]) => void;
  selectedCount: number;
}

const STATUSES = ['scheduled', 'in-progress', 'completed', 'delayed', 'cancelled'];
const TYPES = ['Replacement', 'Repair', 'Installation', 'Assessment', 'Coating', 'Inspection', 'Emergency', 'New Construction', 'Upgrade'];

function MultiSelect({ label, options, selected, onChange }: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        suppressHydrationWarning
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-body rounded-md border transition-standard ${
          selected.length > 0 ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-border'
        }`}
      >
        {label}
        {selected.length > 0 && <span className="bg-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-caption">{selected.length}</span>}
        <Icon name="ChevronDownIcon" size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-md shadow-xl z-30 py-1">
          {options.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="w-3.5 h-3.5 accent-primary"
              />
              <span className="text-sm font-body text-foreground capitalize">{opt.label}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button onClick={() => { onChange([]); setOpen(false); }} suppressHydrationWarning className="w-full text-left px-3 py-2 text-xs text-error hover:bg-muted font-caption border-t border-border mt-1">
              Clear filter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function FilterBar({ crew, filterStatus, filterCrew, filterType, onStatusChange, onCrewChange, onTypeChange, selectedCount }: FilterBarProps) {
  const hasFilters = filterStatus.length > 0 || filterCrew.length > 0 || filterType.length > 0;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-card border-b border-border flex-shrink-0 flex-wrap">
      <Icon name="FunnelIcon" size={16} className="text-muted-foreground flex-shrink-0" />
      <span className="text-sm font-caption text-muted-foreground mr-1">Filter:</span>

      <MultiSelect
        label="Status"
        options={STATUSES.map(s => ({ value: s, label: s }))}
        selected={filterStatus}
        onChange={onStatusChange}
      />
      <MultiSelect
        label="Crew"
        options={crew.map(c => ({ value: c.id, label: c.name }))}
        selected={filterCrew}
        onChange={onCrewChange}
      />
      <MultiSelect
        label="Type"
        options={TYPES.map(t => ({ value: t, label: t }))}
        selected={filterType}
        onChange={onTypeChange}
      />

      {hasFilters && (
        <button
          onClick={() => { onStatusChange([]); onCrewChange([]); onTypeChange([]); }}
          suppressHydrationWarning
          className="flex items-center gap-1 px-2 py-1.5 text-xs font-caption text-error hover:bg-error/10 rounded-md transition-standard"
        >
          <Icon name="XMarkIcon" size={14} />
          Clear all
        </button>
      )}

      <div className="ml-auto flex items-center gap-3">
        {selectedCount > 0 && (
          <span className="text-sm font-caption text-primary">{selectedCount} selected</span>
        )}
        <div className="flex items-center gap-1.5 text-xs font-caption text-muted-foreground">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block"></span>scheduled
          <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block ml-2"></span>in-progress
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-400 inline-block ml-2"></span>completed
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block ml-2"></span>delayed
          <span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block ml-2"></span>cancelled
        </div>
      </div>
    </div>
  );
}